/**
 * Sensitive-action registry (Task #3066).
 *
 * Single source of truth for the actions that require "step-up auth" — the
 * user must have completed a strong authentication challenge (password,
 * magic-link verify, MFA TOTP verify, or biometric on mobile) within the
 * window below. The API middleware reads this map to gate routes; the web
 * and mobile clients read it to label the re-auth prompt ("Confirm it's you
 * before changing your payout account").
 *
 * Adding a sensitive endpoint:
 *   1. Pick a key here and set its window (default 5 min).
 *   2. Add the `requireStepUp(auth, "<key>")` call inside the route.
 *   3. Add the action's verb to `actionLabel()` so the re-auth modal can
 *      explain WHY the user is being prompted.
 *
 * Why a registry (vs. an inline window per route): the security review
 * boundary is "what set of actions count as sensitive?" — losing one
 * because a route was forgotten is the failure mode. A central map is
 * grep-friendly and easy to audit.
 */

export const STEP_UP_DEFAULT_WINDOW_SEC = 300; // 5 minutes

export type SensitiveAction =
  | "change_password"
  | "set_initial_password"
  | "change_email"
  | "add_payment_method"
  | "remove_payment_method"
  | "set_default_payment_method"
  | "change_payout_method"
  | "set_payout_email"
  | "enroll_mfa"
  | "remove_mfa"
  | "revoke_session"
  | "revoke_all_sessions"
  | "export_data"
  | "delete_account"
  // ── Host deposit finalization ─────────────────────────────────────
  // Capturing a deposit transfers held renter funds to the host;
  // releasing it cancels the hold and forfeits the host's claim.
  // Both are direct, irreversible money mutations — require a fresh
  // strong-auth proof so a stolen or shared session cannot finalize
  // the deposit without the real host re-authenticating.
  | "host_deposit_capture"
  | "host_deposit_release"
  // ── Admin destructive actions (Task #3089, F2 / F5) ──────────────
  // Gated by `requireStepUp(auth, res, "<key>")` on the admin routes
  // listed in the audit so a borrowed/phished admin session can't
  // refund money, wipe trips, or take over accounts without a
  // second proof-of-identity inside the freshness window.
  | "admin_trip_refund"
  | "admin_trip_cancel"
  | "admin_deposit_capture"
  | "admin_deposit_release"
  | "admin_fraud_resolve"
  | "admin_user_suspend"
  | "admin_user_delete"
  | "admin_user_impersonate"
  | "admin_host_payout_mutate"
  // Task #3351 — one-click reconcile of a Booking Snapshot Integrity
  // mismatch (re-syncs trip fee columns / payment-schedule snapshot to the
  // accepted suggestion or row). Money-affecting classes are step-up gated
  // so a borrowed admin session can't silently rewrite what a trip charges.
  | "admin_snapshot_reconcile"
  // BL-017 — permanent listing removal by an admin (host-facing DELETE
  // was retired; hosts can only take a listing offline via
  // PUT /listings/:id/pause, so removal is now an admin-gated path
  // with step-up + audit-log + required reason).
  | "admin_listing_remove"
  // ── Admin money / DDL actions (Task #3886, S-C2/H4/M6) ───────────
  // Production DDL execution / one-click push / rollback / prod-sync,
  // manual cron (Inngest) firing, paid background-check submission, and
  // the charge-capturing admin trip-extension response. Step-up so a
  // borrowed admin session can't run prod schema changes, fire crons,
  // spend money on background checks, or push through a trip-extension
  // charge without a fresh proof-of-identity.
  | "admin_schema_ddl"
  | "admin_cron_trigger"
  | "admin_background_check"
  | "admin_trip_extension"
  // Task #4333 — promoting legacy booking-import "ready" rows into live,
  // already-paid `confirmed` trips (fires renter/host confirmation emails +
  // push). Money-affecting live bookings, so step-up gated: a borrowed admin
  // session can't promote duplicate paid trips without a fresh proof-of-identity.
  | "admin_booking_import_approve"
  // ── Admin config / account actions (Task #4441) ──────────────────
  // Role/permission management, destructive 360 account actions (reset
  // password, force-logout, merge, credit, messaging), insurance pricing
  // config writes/imports, query-cache busting, vehicle-DB catalogue
  // writes, and driver-verification moderation. Step-up so a borrowed or
  // phished admin session can't grant itself permissions, take over an
  // account, rewrite insurance pricing, flush caches, or push driver
  // approvals through without a fresh proof-of-identity.
  | "admin_roles_manage"
  | "admin_user_account_action"
  | "admin_insurance_write"
  | "admin_cache_bust"
  | "admin_vehicle_db_write"
  | "admin_driver_moderate"
  // Claim verification manifests contain regulated provider lineage and
  // immutable evidence hashes. Exporting one requires a fresh proof of
  // identity independent from ordinary claim moderation.
  | "admin_claim_manifest_export"
  // Task #20005 — Insurance Flow DSv2: resolving a damage claim can move
  // money (deposit capture / payout deduction), so it re-proves identity.
  | "admin_claim_resolve";

export interface SensitiveActionConfig {
  /** Maximum age of `last_strong_auth_at` (in seconds) at which the action is allowed. */
  windowSec: number;
  /** Short imperative used in the re-auth prompt: "Confirm it's you before <label>." */
  label: string;
}

export const SENSITIVE_ACTIONS: Record<SensitiveAction, SensitiveActionConfig> = {
  host_deposit_capture:      { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "capturing this security deposit" },
  host_deposit_release:      { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "releasing this security deposit" },
  change_password:           { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "changing your password" },
  set_initial_password:      { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "setting your password" },
  change_email:              { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "changing your email" },
  add_payment_method:        { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "adding a payment method" },
  remove_payment_method:     { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "removing a payment method" },
  set_default_payment_method:{ windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "changing your default payment method" },
  change_payout_method:      { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "changing your payout account" },
  set_payout_email:          { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "changing your payout email" },
  enroll_mfa:                { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "turning on two-factor auth" },
  remove_mfa:                { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "turning off two-factor auth" },
  revoke_session:            { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "signing out another device" },
  revoke_all_sessions:       { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "signing out all other devices" },
  export_data:               { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "exporting your personal data" },
  delete_account:            { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "deleting your account" },
  // ── Admin destructive actions (Task #3089) ──────────────────────
  admin_trip_refund:         { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "refunding this trip" },
  admin_trip_cancel:         { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "cancelling this trip" },
  admin_deposit_capture:     { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "capturing this deposit" },
  admin_deposit_release:     { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "releasing this deposit" },
  admin_fraud_resolve:       { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "resolving this fraud-review case" },
  admin_user_suspend:        { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "suspending this user" },
  admin_user_delete:         { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "deleting this user" },
  admin_user_impersonate:    { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "impersonating this user" },
  admin_host_payout_mutate:  { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "changing this host's payout" },
  admin_snapshot_reconcile:  { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "reconciling this booking snapshot" },
  admin_listing_remove:      { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "permanently removing this listing" },
  // ── Admin money / DDL actions (Task #3886) ──────────────────────
  admin_schema_ddl:          { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "executing this production schema change" },
  admin_cron_trigger:        { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "triggering this background job" },
  admin_background_check:    { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "submitting this background check" },
  admin_trip_extension:      { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "responding to this trip extension" },
  // Task #4333 — legacy booking-import approve (promotes rows to live paid trips).
  admin_booking_import_approve: { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "approving this booking import" },
  // ── Admin config / account actions (Task #4441) ─────────────────
  admin_roles_manage:        { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "managing admin roles" },
  admin_user_account_action: { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "performing this account action" },
  admin_insurance_write:     { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "changing insurance pricing" },
  admin_cache_bust:          { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "flushing this cache" },
  admin_vehicle_db_write:    { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "editing the vehicle database" },
  admin_driver_moderate:     { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "moderating this driver" },
  admin_claim_manifest_export: { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "exporting this claim verification evidence" },
  admin_claim_resolve:       { windowSec: STEP_UP_DEFAULT_WINDOW_SEC, label: "resolving this damage claim" },
};

export function isSensitiveAction(value: string): value is SensitiveAction {
  return Object.prototype.hasOwnProperty.call(SENSITIVE_ACTIONS, value);
}

export function getSensitiveActionConfig(action: SensitiveAction): SensitiveActionConfig {
  return SENSITIVE_ACTIONS[action];
}

/**
 * Standard error shape returned by the API when a sensitive route is hit
 * outside the freshness window. Both clients pattern-match on `error` to
 * trigger the re-auth modal; `action` lets them render the "why" copy.
 */
export interface StepUpRequiredError {
  error: "step_up_required";
  action: SensitiveAction;
  windowSec: number;
  /** Server-side "now" minus user's last_strong_auth_at; null when never authed strongly. */
  staleSec: number | null;
}

export const STEP_UP_REQUIRED_ERROR = "step_up_required" as const;
